/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { Fragment } from 'react';
import {
  Modal,
  Input,
  Checkbox,
  Table,
  Switch,
  Tag as TagAntd,
  Collapse,
  message,
} from 'antd';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ModalProps } from 'antd/lib/modal';

import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { TableProps } from 'antd/lib/table';
import { useMemo, CSSProperties, useState } from 'react';

import IconButton from '~components/_common/IconButton/IconButton';
import { CreateEditTagModal } from '~components/popups-modals/CreateEditTag.modal';
import { DeleteTagModal } from '~components/popups-modals/DeleteTag.modal';
import { RestoreTagModal } from '~components/popups-modals/RestoreTag.modal';

import { fadeInKeyframes } from '~src/styles/animations';

import { DB_STATE } from '~src/types/enums';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { Tag } from '~src/services/api/archiveSettings';
import { appLog } from '~src/utils/log';
import { tabStyleFn } from '../RightsModal/Tabs/tabs.util';
import { useTenderFolders } from '~src/hooks/api-hooks/useTenderFolders';
import { DbTenderFolder } from '~src/types/db';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import FolderTenderHammer from '~src/assets/nav-icons/folder-tender-hammer.svg';
import FolderTenderHammerNoColor from '~src/assets/nav-icons/folder-tender-hammer-no-color.svg';

import { queryCache, useMutation } from 'react-query';
import { linkUnlinkTendereFolder } from '~src/services/api/tender';
import { useProjectId } from '~src/hooks/useProjectId';

interface TenderFolderModalProps extends ModalProps {
  tenderId: string;
}

export const TenderFolderModal: React.FC<TenderFolderModalProps> = ({
  onCancel,
  afterClose,
  tenderId,
  ...modalProps
}) => {
  const { Panel } = Collapse;
  const projectId = useProjectId();

  const intl = useIntl();
  const style = useStyles(styleFn);
  const foldersQuery = useTenderFolders({ tender_id_or_unassigned: tenderId });
  const [doLinkUnlinkTenderFolder] = useMutation(linkUnlinkTendereFolder, {
    onError: () => {
      message.error(intl.formatMessage({ id: 'generic-error' }));
    },
    onSuccess: (data, variables) => {
      message.success(
        intl.formatMessage({
          id:
            variables.action === 'set_folder_to'
              ? 'link-tender-folder-success'
              : 'unlink-tender-folder-success',
        })
      );
      queryCache.invalidateQueries('tendersTenderFolders');
      queryCache.invalidateQueries('tenderFolders');
    },
  });

  const [searchValue, setSearchValue] = useState('');

  const linkedFolders = useMemo(
    () =>
      foldersQuery.data?.filter(
        (folder) => folder.options.tender_registration_id !== null
      ),
    [foldersQuery.data]
  );

  const availableFolders = useMemo(
    () =>
      foldersQuery.data?.filter(
        (folder) =>
          folder.options.tender_registration_id === null &&
          folder.name.toLowerCase().includes(searchValue.toLowerCase())
      ),
    [foldersQuery.data, searchValue]
  );

  const getColorRow = (record) =>
    Number(record.state) === DB_STATE.REMOVED ? '#ffd4d9' : '';

  const columns: TableProps<DbTenderFolder>['columns'] = [
    {
      key: 'folder',
      title: '',
      dataIndex: 'folder',
      width: 15,
      onCell: (record) => ({
        style: {
          whiteSpace: 'nowrap',
          overflow: 'auto',
          textAlign: 'center',
          justifyContent: 'center',
          display: 'flex',
        },
      }),
      render: (title, node) => (
        <div css={style.actionContainer}>
          <NavIcon customStyle={{ width: '22px' }} icon={FolderTenderHammer} />
        </div>
      ),
    },
    {
      key: 'name',
      title: intl.formatMessage({ id: 'name' }),
      dataIndex: 'name',
      width: 80,
      onCell: (record) => ({
        style: {
          whiteSpace: 'nowrap',
          overflow: 'auto',
          background: getColorRow(record),
        },
      }),
      render: (title) => <span>{title}</span>,
    },
    {
      key: 'action',
      title: intl.formatMessage({ id: 'action' }),
      dataIndex: 'action',
      width: 45,
      align: 'center',
      onCell: (record) => ({
        style: {
          whiteSpace: 'nowrap',
          overflow: 'auto',
          background: getColorRow(record),
          textAlign: 'center',
        },
      }),
      render: (title, record) => (
        <div
          onClick={() => {
            appLog('');
          }}
          css={[style.actionContainer, style.editColor]}>
          <Checkbox
            checked={!!record?.options?.tender_registration_id}
            onChange={(e) => {
              doLinkUnlinkTenderFolder({
                action: record?.options?.tender_registration_id
                  ? 'remove_folder_from'
                  : 'set_folder_to',
                tenderId,
                projectId: String(projectId),
                tenderFolderId: String(record.id),
              });
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <Modal
      okButtonProps={{ hidden: true }}
      title={
        <div css={style.titleContainer}>
          <NavIcon
            customStyle={{ width: '36px', height: '22px', marginRight: '4px' }}
            icon={FolderTenderHammerNoColor}
          />
          {intl.formatMessage({ id: 'tender-folders' })}
        </div>
      }
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width="min(75vw, 450px)"
      bodyStyle={{ padding: '0 25px 0 25px' }}
      centered
      destroyOnClose
      maskClosable={false}
      onCancel={onCancel}
      footer={
        window.sessionStorage.getItem('appState') !== 'timeMachine' && undefined
      }
      {...modalProps}>
      <div css={style.container}>
        <div css={style.rowContainer}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '0px',
            }}>
            <h3 css={style.heading}>
              {intl.formatMessage({ id: 'tender-folders' })}
            </h3>
            <IconButton
              icon={['far', 'info-circle']}
              customStyle={{ fontSize: '130%' }}
              containerStyle={{
                marginLeft: 0,
                color: '#7a829b',
              }}
              backgroundOff
              tooltip={
                <div>
                  {intl.formatMessage({ id: 'tender-folders-help-tooltip' })}
                </div>
              }
              widthTooltip="300px"
            />
          </div>
        </div>
        <Collapse
          expandIconPosition={'right'}
          defaultActiveKey={['1', '2']}
          ghost>
          <Panel
            header={
              <span onClick={(e) => e.stopPropagation()}>
                {intl.formatMessage({ id: 'linked-folders' })}
              </span>
            }
            key="1">
            <Table
              pagination={false}
              columns={columns}
              dataSource={linkedFolders}
              rowKey="id"
              showHeader={false}
              scroll={{ y: 200 }}
            />
          </Panel>
          <Panel
            header={
              <div
                style={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
                onClick={(e) => e.stopPropagation()}>
                <span>{intl.formatMessage({ id: 'available-folders' })}</span>
                <div css={[style.containerSearch]} key="searchBarContainer">
                  <Input
                    prefix={
                      <FontAwesomeIcon
                        icon={['far', 'search']}
                        style={style.icons as CSSProperties}
                      />
                    }
                    css={style.inputField}
                    placeholder={intl.formatMessage({ id: 'search' })}
                    allowClear
                    onChange={(e) => setSearchValue(e.target.value)}
                  />
                </div>
              </div>
            }
            key="2">
            <Table
              pagination={false}
              columns={columns}
              dataSource={availableFolders}
              rowKey="id"
              showHeader={false}
              scroll={{ y: 220 }}
            />
          </Panel>
        </Collapse>
      </div>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {},
  iconRemove: {},
  rowContainer: { marginBottom: '6px' },

  actionContainer: {
    cursor: 'pointer',
  },
  containerSearch: {
    width: '50%',
    // display: 'flex',
    // alignItems: 'center',
    // border: `1px solid ${theme.colors.lightGray}`,
    // borderRadius: '2px',
    // margin: '8px 0',
    // paddingLeft: '5px',
    // width: '55%',
    // height: '32px',
    // ':focus': {
    //   border: `1px solid ${theme.colors.primary}`,
    // },
    // // backgroundColor: theme.colors.gray,
  },
  icons: {
    color: theme.colors.text.primary,
    fontSize: '16px',
  },
  inputField: {
    // border: 'none!important',
    // height: '100%',
    // width: '100%',
    // color: `${theme.colors.text.primary}`,
    //
    // '::placeholder': {
    //   color: `${theme.colors.text.secondary}`,
    // },
    //
    // ':focus': {
    //   outline: 'none',
    // },
  },
  buttonDiv: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    type: 'button',
    cursor: 'pointer',
    marginLeft: 4,
    padding: '2px 10px',
    ':hover': {
      backgroundColor: theme.colors.gray,
      borderRadius: 9,
    },
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: '18px',
    fontWeight: 'bold',
  },
});
